import { action } from 'mobx';
import { uniqBy } from 'lodash';
import Collection from 'core/model/Collection';
import { Model } from 'core/model/Model';
import DashboardModel from 'app/stores/dashboard/DashboardModel';
import SavedView from 'app/stores/savedView/SavedView';
import BoardModel from 'app/stores/boards/BoardModel';

const getLibraryKey = (model) => `${model.type}||${model.id}`;

class LibraryCollection extends Collection {
  constructor(data = [], options = {}) {
    // Base constructor adds data before subclass fields (our `add` override) exist, so defer it until they do.
    super([], options);

    if (!options.models) {
      this.set(data);
    }
  }

  fetch = () =>
    // collection is not fetchable
    Promise.resolve();

  get defaultSortState() {
    return {
      field: 'lastViewedDate',
      direction: 'desc'
    };
  }

  get filterFieldWhitelist() {
    return new Set(['name']);
  }

  // invokes getLibraryKey for de-duplication, since ids can collide across types
  add = action((data) => {
    const rows = Array.isArray(data) ? data : [data];

    const models = rows.map((row) => {
      if (row instanceof Model) {
        row.collection = this;
        return row;
      }
      return this.build(row);
    });

    this.original = uniqBy(this.get().concat(models), getLibraryKey);
    this.filter();
    this.hashModelsById();
    this.setLastUpdated();

    return models;
  });

  // Removes by reference since `remove(id)` is ambiguous when ids collide across types
  removeModel = action((model) => {
    if (this.original) {
      this.original = this.original.filter((m) => m !== model);
    }

    this.models.remove(model);

    if (Array.isArray(this.selected)) {
      const selected = this.selected.filter((m) => m !== model);
      this.selected = selected.length > 0 ? selected : null;
    } else if (this.selected === model) {
      this.clearSelection();
    }

    this.hashModelsById();
    this.setLastUpdated();
  });

  build = (attributes, options = {}) => {
    const { select = false, deserialize = true } = options;
    const ModelClass =
      // eslint-disable-next-line no-nested-ternary
      attributes.type === 'dashboard' ? DashboardModel : attributes.type === 'saved-view' ? SavedView : BoardModel;
    const model = new ModelClass();

    model.store = this.store;
    model.collection = this;
    model.set(deserialize ? model.deserialize(attributes) : attributes);

    if (select) {
      this.selected = model;
    }

    return model;
  };
}

export default LibraryCollection;
